import axios from 'axios';
import { toast } from 'react-toastify';

export const MENUS_REQUEST = 'MENUS_REQUEST';
export const MENUS_SUCCESS = 'MENUS_SUCCESS';
export const MENUS_FAILURE = 'MENUS_FAILURE';
export const MENU_DEFAULT_SUCCESS = 'MENU_DEFAULT_SUCCESS';
export const MENU_DETAIL_REQUEST = 'MENU_DETAIL_REQUEST';
export const MENU_DETAIL_SUCCESS = 'MENU_DETAIL_SUCCESS';
export const MENU_DETAIL_FAILURE = 'MENU_DETAIL_FAILURE';
export const MENU_MUTATION_PENDING = 'MENU_MUTATION_PENDING';
export const MENU_MUTATION_DONE = 'MENU_MUTATION_DONE';

const requestMenus = () => ({ type: MENUS_REQUEST });
const receiveMenus = (menus, defaultMenuId = null, useMenuSchedule = false) => ({
  type: MENUS_SUCCESS,
  payload: { menus, defaultMenuId, useMenuSchedule },
});
const failMenus = (err) => ({ type: MENUS_FAILURE, payload: err });
const receiveDefaultMenu = (defaultMenuId, useMenuSchedule = false) => ({
  type: MENU_DEFAULT_SUCCESS,
  payload: { defaultMenuId, useMenuSchedule },
});
const requestMenuDetail = (menuId) => ({ type: MENU_DETAIL_REQUEST, payload: { menuId } });
const receiveMenuDetail = (menu) => ({ type: MENU_DETAIL_SUCCESS, payload: { menu } });
const failMenuDetail = (err) => ({ type: MENU_DETAIL_FAILURE, payload: err });
const mutationPending = () => ({ type: MENU_MUTATION_PENDING });
const mutationDone = () => ({ type: MENU_MUTATION_DONE });

export function loadMenus(includeInactive = false) {
  return async (dispatch) => {
    dispatch(requestMenus());
    try {
      const { data } = await axios.get(`/api/menu/list?includeInactive=${includeInactive ? 'true' : 'false'}`);
      if (data.status === 'Ok') {
        dispatch(receiveMenus(
          data.menus || [],
          data.defaultMenuId ?? data.DefaultMenuId ?? null,
          !!(data.useMenuSchedule ?? data.UseMenuSchedule),
        ));
      } else {
        toast.error(data.message || 'Failed to load menus');
        dispatch(failMenus(data.message));
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to load menus';
      toast.error(msg);
      dispatch(failMenus(msg));
    }
  };
}

export function loadMenuDetail(menuId) {
  return async (dispatch) => {
    dispatch(requestMenuDetail(menuId));
    try {
      const { data } = await axios.get(`/api/menu/${menuId}`);
      if (data.status === 'Ok') {
        dispatch(receiveMenuDetail(data.menu));
        return data.menu;
      }
      toast.error(data.message || 'Failed to load menu');
      dispatch(failMenuDetail(data.message));
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to load menu';
      toast.error(msg);
      dispatch(failMenuDetail(msg));
    }
    return null;
  };
}

export function createMenu(model) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      const { data } = await axios.post('/api/menu/create', model);
      if (data.status === 'Ok') {
        toast.success('Menu created');
        await dispatch(loadMenus(true));
        return data.menuId;
      }
      toast.error(data.message || 'Failed to create menu');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to create menu');
    } finally {
      dispatch(mutationDone());
    }
    return null;
  };
}

export function updateMenu(menuId, model) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      const { data } = await axios.put(`/api/menu/${menuId}`, model);
      if (data.status === 'Ok') {
        toast.success('Menu updated');
        await dispatch(loadMenus(true));
        return true;
      }
      toast.error(data.message || 'Failed to update menu');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to update menu');
    } finally {
      dispatch(mutationDone());
    }
    return false;
  };
}

/** Persist SortOrder = index for each menu in the given display order. Silent (no per-row toast). */
export function persistMenuSortOrders(orderedMenus) {
  return async (dispatch) => {
    if (!orderedMenus || !orderedMenus.length) return false;
    dispatch(mutationPending());
    try {
      const updates = orderedMenus
        .map((m, index) => ({ m, index }))
        .filter(({ m, index }) => (m.SortOrder || 0) !== index);

      for (const { m, index } of updates) {
        const { data } = await axios.put(`/api/menu/${m.MenuId}`, {
          MenuId: m.MenuId,
          Name: m.Name,
          SortOrder: index,
          IsActive: !!m.IsActive,
        });
        if (data.status !== 'Ok') {
          toast.error(data.message || 'Failed to update menu order');
          await dispatch(loadMenus(true));
          return false;
        }
      }

      await dispatch(loadMenus(true));
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to update menu order');
      await dispatch(loadMenus(true));
      return false;
    } finally {
      dispatch(mutationDone());
    }
  };
}

export function archiveMenu(menuId) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      const { data } = await axios.delete(`/api/menu/${menuId}`);
      if (data.status === 'Ok') {
        toast.success('Menu archived');
        await dispatch(loadMenus(true));
        return true;
      }
      toast.error(data.message || 'Failed to archive menu');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to archive menu');
    } finally {
      dispatch(mutationDone());
    }
    return false;
  };
}

export function restoreMenu(menuId) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      const { data } = await axios.post(`/api/menu/${menuId}/restore`);
      if (data.status === 'Ok') {
        toast.success('Menu restored');
        await dispatch(loadMenus(true));
        return true;
      }
      toast.error(data.message || 'Failed to restore menu');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to restore menu');
    } finally {
      dispatch(mutationDone());
    }
    return false;
  };
}

/**
 * Merchant-wide default for POS/kiosk stations without an override.
 * Pass null (or 0) for All / full catalog. Auto schedule is independent:
 * omit useSchedule to keep the current Auto flag; pass true/false to toggle it.
 */
export function setMerchantDefaultMenu(menuId, options = {}) {
  return async (dispatch, getState) => {
    dispatch(mutationPending());
    try {
      const prev = getState().menus || {};
      const useSchedule = options.useSchedule !== undefined
        ? !!options.useSchedule
        : !!prev.useMenuSchedule;
      const payload = {
        MenuId: menuId == null || Number(menuId) <= 0 ? null : Number(menuId),
        UseSchedule: useSchedule,
      };
      const { data } = await axios.put('/api/menu/default', payload);
      if (data.status === 'Ok') {
        const nextId = data.defaultMenuId ?? data.DefaultMenuId ?? null;
        const nextUse = !!(data.useMenuSchedule ?? data.UseMenuSchedule ?? useSchedule);
        dispatch(receiveDefaultMenu(
          nextId == null || Number(nextId) <= 0 ? null : Number(nextId),
          nextUse,
        ));
        const autoChanged = nextUse !== !!prev.useMenuSchedule;
        toast.success(
          autoChanged
            ? (nextUse ? 'Auto schedule on' : 'Auto schedule off')
            : nextId
              ? 'Default menu updated for all stations'
              : 'Default menu cleared (stations show full catalog unless overridden)'
        );
        return true;
      }
      toast.error(data.message || 'Failed to set default menu');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to set default menu');
    } finally {
      dispatch(mutationDone());
    }
    return false;
  };
}

/**
 * Station menu: inherit merchant default, Auto schedule, force All, or a specific menu.
 * @param {number} stationId
 * @param {{ inherit: boolean, useSchedule?: boolean, menuId?: number|null }} selection
 */
export function saveStationMenu(stationId, selection) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      let body;
      if (selection?.inherit) {
        body = { Inherit: true, MenuId: null, UseSchedule: false };
      } else if (selection?.useSchedule) {
        body = { Inherit: false, MenuId: null, UseSchedule: true };
      } else {
        body = {
          Inherit: false,
          UseSchedule: false,
          MenuId: selection?.menuId == null || Number(selection.menuId) <= 0
            ? null
            : Number(selection.menuId),
        };
      }
      const { data } = await axios.put(`/api/menu/station/${stationId}`, body);
      if (data.status === 'Ok') {
        toast.success('Station menu updated');
        return true;
      }
      toast.error(data.message || 'Failed to update station menu');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to update station menu');
    } finally {
      dispatch(mutationDone());
    }
    return false;
  };
}

/** @returns {Promise<Array>} schedule rows for a menu (or all merchant schedules). */
export async function fetchMenuSchedules(menuId = null) {
  const qs = menuId != null && Number(menuId) > 0 ? `?menuId=${Number(menuId)}` : '';
  const { data } = await axios.get(`/api/menu/schedules${qs}`);
  if (data.status !== 'Ok') {
    throw new Error(data.message || 'Failed to load schedules');
  }
  return data.schedules || data.Schedules || [];
}

/**
 * Replace all schedule windows for one menu.
 * @param {number} menuId
 * @param {Array<{ DayOfWeek: number, StartTime: string, EndTime: string }>} schedules
 */
export function replaceMenuSchedules(menuId, schedules) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      const { data } = await axios.put(`/api/menu/${menuId}/schedules`, {
        Schedules: (schedules || []).map((s) => ({
          DayOfWeek: Number(s.DayOfWeek),
          StartTime: normalizeTimeForApi(s.StartTime),
          EndTime: normalizeTimeForApi(s.EndTime),
          IsActive: true,
        })),
      });
      if (data.status === 'Ok') {
        toast.success('Schedule saved');
        const saved = data.schedules || data.Schedules || [];
        return saved.length > 0 ? saved : (schedules || []);
      }
      toast.error(data.message || 'Failed to save schedule');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to save schedule');
    } finally {
      dispatch(mutationDone());
    }
    return null;
  };
}

function normalizeTimeForApi(value) {
  const raw = String(value || '').trim();
  if (!raw) return '00:00:00';
  // Accept HH:mm or HH:mm:ss
  const parts = raw.split(':');
  const h = String(Math.min(23, Math.max(0, Number(parts[0]) || 0))).padStart(2, '0');
  const m = String(Math.min(59, Math.max(0, Number(parts[1]) || 0))).padStart(2, '0');
  const sec = parts.length > 2
    ? String(Math.min(59, Math.max(0, Number(parts[2]) || 0))).padStart(2, '0')
    : '00';
  return `${h}:${m}:${sec}`;
}

export function replaceMenuCategories(menuId, categoryIds) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      const { data } = await axios.put(`/api/menu/${menuId}/categories`, { CategoryIds: categoryIds });
      if (data.status === 'Ok') {
        toast.success('Categories saved');
        await dispatch(loadMenuDetail(menuId));
        return true;
      }
      toast.error(data.message || 'Failed to save categories');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to save categories');
    } finally {
      dispatch(mutationDone());
    }
    return false;
  };
}

export function upsertMenuPrices(menuId, prices) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      const { data } = await axios.put(`/api/menu/${menuId}/prices`, { Prices: prices });
      if (data.status === 'Ok') {
        toast.success('Prices saved');
        await dispatch(loadMenuDetail(menuId));
        return true;
      }
      toast.error(data.message || 'Failed to save prices');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to save prices');
    } finally {
      dispatch(mutationDone());
    }
    return false;
  };
}

export function clearMenuPrice(menuId, productId, variationId = 0) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      const vid = Number(variationId) || 0;
      const url = vid > 0
        ? `/api/menu/${menuId}/prices/${productId}/${vid}`
        : `/api/menu/${menuId}/prices/${productId}`;
      const { data } = await axios.delete(url);
      if (data.status === 'Ok') {
        toast.success('Override cleared');
        await dispatch(loadMenuDetail(menuId));
        return true;
      }
      toast.error(data.message || 'Failed to clear override');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to clear override');
    } finally {
      dispatch(mutationDone());
    }
    return false;
  };
}

export function upsertMenuOptionPrices(menuId, prices) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      const { data } = await axios.put(`/api/menu/${menuId}/option-prices`, { Prices: prices });
      if (data.status === 'Ok') {
        toast.success('Option prices saved');
        await dispatch(loadMenuDetail(menuId));
        return true;
      }
      toast.error(data.message || 'Failed to save option prices');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to save option prices');
    } finally {
      dispatch(mutationDone());
    }
    return false;
  };
}

export function clearMenuOptionPrice(menuId, optionId) {
  return async (dispatch) => {
    dispatch(mutationPending());
    try {
      const { data } = await axios.delete(`/api/menu/${menuId}/option-prices/${optionId}`);
      if (data.status === 'Ok') {
        toast.success('Option override cleared');
        await dispatch(loadMenuDetail(menuId));
        return true;
      }
      toast.error(data.message || 'Failed to clear option override');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to clear option override');
    } finally {
      dispatch(mutationDone());
    }
    return false;
  };
}
